import { useMemoizeArg } from "@r360/library";
import { useMemo } from "react";
import { RootState } from "..";
import { resvTypeByLid } from "../Selectors";
import { TReservationTypeCriterias } from "../store/types";
import useAppSelector from "./useAppSelector";

/**
 * Custom hook to filter reservation type criteria based on reservation type ID and channel ID.
 *
 * @param {number} reservationTypeId - The ID of the reservation type.
 * @param {number} [channellid=0] - The ID of the channel (default is 0).
 * @returns {TReservationTypeCriterias} - The filtered reservation type criteria.
 **/
const useReservationTypeCriterias = (reservationTypeId: number, channellid = 0) => {
    const reservationTypeCriterias: TReservationTypeCriterias =
        useAppSelector((state: RootState) => resvTypeByLid(state, reservationTypeId))?.criterias || {};

    const filteredCriterias: TReservationTypeCriterias = useMemo(() => {
        return Object.entries(reservationTypeCriterias).reduce((acc, [key, presentationTypeCriterias]) => {
            const usesSeparateChannelCriterias = presentationTypeCriterias.some(item => item.channellid !== 0);

            let presentationTypeCriteriasForChannel = presentationTypeCriterias;

            if (usesSeparateChannelCriterias) {
                presentationTypeCriteriasForChannel = presentationTypeCriterias.filter(
                    item => item.channellid === channellid || item.forceAllChannels
                );
            }

            if (presentationTypeCriteriasForChannel.length > 0) {
                acc[key] = presentationTypeCriteriasForChannel;
            }

            return acc;
        }, {} as TReservationTypeCriterias);
    }, [useMemoizeArg(reservationTypeCriterias), channellid]);

    return filteredCriterias;
};

export default useReservationTypeCriterias;
